import { ref, computed } from 'vue'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, setDoc, onSnapshot } from 'firebase/firestore'
import { auth, db } from '@/firebase'
import { devLog, shouldEnableDebugMenu } from '@/utils/environment'
import { useAuthStore } from '@/store/authStore'
import { assertCanWriteDistrictSettings } from '@/utils/schoolSettings'

// Dev/test app only — hardened production builds ignore these flags entirely.
const DEFAULT_SMOKE_TEST = true
const DEFAULT_ROLE_SWITCHER = true
const ALLOWED_SMOKE_PROJECT_IDS = ['casemangervue']

export function isSmokeRunnerEnvironment() {
  return (
    shouldEnableDebugMenu() &&
    ALLOWED_SMOKE_PROJECT_IDS.includes(import.meta.env.VITE_FIREBASE_PROJECT_ID)
  )
}

class DebugToolsSettingsManager {
  constructor() {
    this.smokeTestEnabled = ref(DEFAULT_SMOKE_TEST)
    this.roleSwitcherEnabled = ref(DEFAULT_ROLE_SWITCHER)
    this.unsubscribeSettings = null
    this.init()
  }

  init() {
    try {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          this.loadSettings()
        } else {
          if (this.unsubscribeSettings) {
            this.unsubscribeSettings()
            this.unsubscribeSettings = null
          }
          this.applyDefaults()
        }
      })
    } catch {
      // Unit tests stub Auth. The import log and smoke tools still load.
      this.applyDefaults()
    }
  }

  loadSettings() {
    if (!shouldEnableDebugMenu()) {
      this.applyDefaults()
      return
    }

    try {
      if (this.unsubscribeSettings) {
        this.unsubscribeSettings()
        this.unsubscribeSettings = null
      }

      const settingsRef = doc(db, 'app_settings', 'security')

      this.unsubscribeSettings = onSnapshot(
        settingsRef,
        (snap) => {
          const data = snap.exists() ? snap.data() : {}
          this.smokeTestEnabled.value =
            data.debugSmokeTestEnabled !== undefined
              ? data.debugSmokeTestEnabled === true
              : DEFAULT_SMOKE_TEST
          this.roleSwitcherEnabled.value =
            data.debugRoleSwitcherEnabled !== undefined
              ? data.debugRoleSwitcherEnabled === true
              : DEFAULT_ROLE_SWITCHER

          devLog(
            `🛠 Debug tools settings: smokeTest=${this.smokeTestEnabled.value}, roleSwitcher=${this.roleSwitcherEnabled.value}`
          )
        },
        (error) => {
          console.error('Failed to load debug tools settings:', error)
          this.applyDefaults()
        }
      )
    } catch (error) {
      console.error('Failed to setup debug tools settings listener:', error)
      this.applyDefaults()
    }
  }

  applyDefaults() {
    this.smokeTestEnabled.value = DEFAULT_SMOKE_TEST
    this.roleSwitcherEnabled.value = DEFAULT_ROLE_SWITCHER
  }

  async updateSettings({ smokeTestEnabled, roleSwitcherEnabled }) {
    if (!shouldEnableDebugMenu()) return

    try {
      assertCanWriteDistrictSettings(useAuthStore().currentUser?.role)
      const settingsRef = doc(db, 'app_settings', 'security')
      await setDoc(
        settingsRef,
        {
          debugSmokeTestEnabled: smokeTestEnabled,
          debugRoleSwitcherEnabled: roleSwitcherEnabled,
          updatedAt: new Date().toISOString(),
          updatedBy: auth.currentUser?.uid
        },
        { merge: true }
      )

      this.smokeTestEnabled.value = smokeTestEnabled
      this.roleSwitcherEnabled.value = roleSwitcherEnabled

      devLog(
        `✅ Debug tools settings updated: smokeTest=${smokeTestEnabled}, roleSwitcher=${roleSwitcherEnabled}`
      )
    } catch (error) {
      console.error('Failed to update debug tools settings:', error)
      throw error
    }
  }
}

const debugToolsSettingsManager = new DebugToolsSettingsManager()

export function useDebugToolsSettings() {
  const smokeTestVisible = computed(
    () => isSmokeRunnerEnvironment() && debugToolsSettingsManager.smokeTestEnabled.value
  )

  const roleSwitcherVisible = computed(
    () => shouldEnableDebugMenu() && debugToolsSettingsManager.roleSwitcherEnabled.value
  )

  return {
    smokeTestEnabled: debugToolsSettingsManager.smokeTestEnabled,
    roleSwitcherEnabled: debugToolsSettingsManager.roleSwitcherEnabled,
    smokeTestVisible,
    roleSwitcherVisible,
    updateSettings: debugToolsSettingsManager.updateSettings.bind(debugToolsSettingsManager)
  }
}

/** Non-reactive check — prefer smokeTestVisible from useDebugToolsSettings(). */
export function isSmokeRunnerAvailable() {
  return isSmokeRunnerEnvironment() && debugToolsSettingsManager.smokeTestEnabled.value
}
